import React, { createContext, ReactNode, useCallback, useContext, useReducer } from "react";
import { flushSync } from "react-dom";

interface Message {
  chatId: string;
  messageId: string;
  streamId: string;
  content: string;
  role: string;
}

interface State {
  messages: Record<string, Message[]>;
  loading: boolean;
  error: string | null;
  chatId: string | null;
  streamId: string | null;
  eventSource: EventSource | null;
  charsPerSec: number;
}

const initialState: State = {
  messages: {},
  loading: false,
  error: null,
  chatId: null,
  streamId: null,
  eventSource: null,
  charsPerSec: 0,
};

const SET_LOADING = "SET_LOADING";
const SET_ERROR = "SET_ERROR";
const SET_CHAT_ID = "SET_CHAT_ID";
const SET_STREAM_ID = "SET_STREAM_ID";
const SET_EVENT_SOURCE = "SET_EVENT_SOURCE";
const SET_CHARS_PER_SEC = "SET_CHARS_PER_SEC";
const ADD_MESSAGE = "ADD_MESSAGE";
const UPDATE_MESSAGE = "UPDATE_MESSAGE";
const MULTI_ADD_MESSAGE = "MULTI_ADD_MESSAGE";

type Action =
  | { type: typeof SET_LOADING; payload: boolean }
  | { type: typeof SET_ERROR; payload: string | null }
  | { type: typeof SET_CHAT_ID; payload: string }
  | { type: typeof SET_STREAM_ID; payload: string }
  | { type: typeof SET_EVENT_SOURCE; payload: EventSource | null }
  | { type: typeof SET_CHARS_PER_SEC; payload: number }
  | { type: typeof ADD_MESSAGE; payload: Message }
  | { type: typeof UPDATE_MESSAGE; payload: { chatId: string; content: string; messageId: string } }
  | { type: typeof MULTI_ADD_MESSAGE; payload: { chatId: string; messages: Message[] } };

const chatReducer = (state: State, action: Action): State => {
  switch (action.type) {
    case SET_LOADING:
      return { ...state, loading: action.payload };
    case SET_ERROR:
      return { ...state, error: action.payload };
    case SET_CHAT_ID:
      return { ...state, chatId: action.payload };
    case SET_STREAM_ID:
      return { ...state, streamId: action.payload };
    case SET_EVENT_SOURCE:
      return { ...state, eventSource: action.payload };
    case SET_CHARS_PER_SEC:
      return { ...state, charsPerSec: action.payload };
    case ADD_MESSAGE: {
      const { chatId, messageId, streamId, content, role } = action.payload;
      return {
        ...state,
        messages: {
          [chatId]: [...(state.messages[chatId] || []), { chatId, messageId, streamId, content, role }],
        },
      };
    }
    case UPDATE_MESSAGE: {
      const { chatId, content, messageId } = action.payload;
      const currentMessages = state.messages[chatId] || [];
      const lastAssistantMessageIndex = currentMessages
        .slice()
        .reverse()
        .findIndex(msg => msg.role === "ASSISTANT");

      if (lastAssistantMessageIndex === -1) {
        return state; // No assistant message found, return state unchanged
      }

      const indexToUpdate = currentMessages.length - 1 - lastAssistantMessageIndex;

      return {
        ...state,
        messages: {
          ...state.messages,
          [chatId]: currentMessages.map((msg, index) =>
            index === indexToUpdate ? { ...msg, content: msg.content + content, messageId } : msg
          ),
        },
      };
    }
    case MULTI_ADD_MESSAGE: {
      const { chatId, messages } = action.payload;
      const currentMessages = state.messages[chatId] || [];

      return {
        ...state,
        messages: {
          ...state.messages,
          [chatId]: [...currentMessages, ...messages],
        },
      };
    }
    default:
      return state;
  }
};

interface ChatAPI {
  sendMessage: (message: string) => Promise<void>;
  startStream: (chat_id: string, stream_id: string) => void;
  stopStream: (source: EventSource) => void;
}

const ChatContextAPI = createContext<ChatAPI | undefined>(undefined);
const ChatMessages = createContext<Message[]>([]);
const ChatState = createContext<State | undefined>(undefined);

ChatContextAPI.displayName = "ChatContextAPI";
ChatMessages.displayName = "ChatMessages";
ChatState.displayName = "ChatState";

export const ChatProvider = ({ children }: { children: ReactNode }) => {
  const [state, dispatch] = useReducer(chatReducer, initialState);

  const sendMessage = useCallback(
    async (message: string) => {
      dispatch({ type: SET_LOADING, payload: true });

      const response = await fetch("/api/chat/message", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          user_id: null,
          chat_id: state.chatId,
          message: message,
          attachments: [],
        }),
      });

      if (!response.ok) {
        console.error("Failed to send message");
        dispatch({ type: SET_ERROR, payload: "Failed to send message." });
        dispatch({ type: SET_LOADING, payload: false });
        return;
      }

      const data = await response.json();
      flushSync(() => {
        dispatch({ type: SET_CHAT_ID, payload: data.customer_message.chat_id });
        dispatch({ type: SET_STREAM_ID, payload: data.stream_id });
        dispatch({
          type: MULTI_ADD_MESSAGE,
          payload: {
            chatId: data.customer_message.chat_id,
            messages: [
              {
                chatId: data.customer_message.chat_id,
                messageId: data.customer_message.id,
                streamId: data.stream_id,
                content: data.customer_message.content,
                role: data.customer_message.role,
              },
              {
                chatId: data.customer_message.chat_id,
                messageId: data.stream_id,
                streamId: data.stream_id,
                content: "",
                role: "ASSISTANT",
              },
            ],
          },
        });
      });

      startStream(data.customer_message.chat_id, data.stream_id);
      dispatch({ type: SET_LOADING, payload: false });
    },
    [state.chatId]
  );

  const startStream = useCallback((chat_id: string, stream_id: string) => {
    dispatch({ type: SET_LOADING, payload: true });
    dispatch({ type: SET_ERROR, payload: null });
    const queryParams = new URLSearchParams({ chat_id, stream_id });
    const source = new EventSource(`/api/chat/stream?${queryParams}`);

    source.onmessage = async event => {
      const data = JSON.parse(event.data);
      const delta = data.choices[0].delta;
      const content = delta.content;
      const finishReason = data.choices[0].finish_reason;
      if (finishReason === "stop") {
        stopStream(source);
        return;
      }

      dispatch({
        type: UPDATE_MESSAGE,
        payload: {
          chatId: chat_id,
          messageId: stream_id, // Use stream_id as messageId for the assistant message
          content,
        },
      });
    };

    source.onerror = () => {
      dispatch({ type: SET_LOADING, payload: false });
      dispatch({ type: SET_ERROR, payload: "Failed to start stream." });
    };

    dispatch({ type: SET_EVENT_SOURCE, payload: source });
  }, []);

  const stopStream = useCallback((source: EventSource) => {
    if (source) {
      source.close();
      dispatch({ type: SET_LOADING, payload: false });
      dispatch({ type: SET_EVENT_SOURCE, payload: null });
    }
  }, []);

  return (
    <ChatContextAPI.Provider
      value={{
        sendMessage,
        startStream,
        stopStream,
      }}
    >
      <ChatState.Provider value={state}>
        <ChatMessages.Provider value={state.chatId ? state.messages[state.chatId] || [] : []}>
          {children}
        </ChatMessages.Provider>
      </ChatState.Provider>
    </ChatContextAPI.Provider>
  );
};

export const useChat = (): ChatAPI => {
  const context = useContext(ChatContextAPI);
  if (!context) {
    throw new Error("useChat must be used within a ChatProvider");
  }
  return context;
};

export const useChatState = (): State => {
  const context = useContext(ChatState);
  if (!context) {
    throw new Error("useChatState must be used within a ChatProvider");
  }
  return context;
};

export const useChatMessages = (): Message[] => useContext(ChatMessages);
